/* eslint-disable @typescript-eslint/no-use-before-define */
import { Button, Checkbox, Ellipsis, Icon, Infobox } from '@zoom/components';
import { intersection } from 'ramda';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';

import { Legend } from '../../../components/Form/Legend';
import { RenderInput } from '../../../components/Inputs/RenderInput';
import { Queue, Skill } from '../../../graphql/components';
import { SortOrder, sortAgentsBy } from '../../../types';
import { formatNameOfPerson } from '../../../utils/helpers';
import { customSort } from '../../../utils/sort';
import {
  PERSONS_BULK_ASSIGN_SEARCH,
  SELECT_ALL_SUITED_PERSONS_CHECKBOX,
  SUITED_PERSONS_RESULT,
  UNSELECT_ALL_SELECTED_PERSONS_CHECKBOX,
  getAssignedSuitedAgentCheckboxTag,
  getAssignedUnSuitedAgentCheckboxTag,
  getSuitedAgentCheckboxTag,
} from '../constants';
// eslint-disable-next-line import-x/no-cycle
import { CheckboxAgent } from './AddPersonsSidebar';
import { AgentDetail } from './AgentDetail';

export interface PersonsBulkAssignProps {
  selectedAgents: Record<string, CheckboxAgent>;
  assignedQueueIds: string[];
  assignedSkillIds: string[];
  queues: Record<string, Queue>;
  skills: Record<string, Skill>;
  setSelectedAgents: React.Dispatch<React.SetStateAction<Record<string, CheckboxAgent>>>;
}

const filterBySearchTerm = (term: string, all: Record<string, CheckboxAgent>) => (id: string) => {
  const fullName = formatNameOfPerson(all[id].name, all[id].surname);
  return fullName.toLowerCase().includes(term.trim().toLowerCase());
};

export const PersonsBulkAssign: React.FC<PersonsBulkAssignProps> = ({
  selectedAgents,
  setSelectedAgents,
  assignedQueueIds,
  assignedSkillIds,
  queues,
  skills,
}) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');

  const allAgentIds = Object.keys(selectedAgents);

  const { assignedIds, unAssignedIds } = Object.keys(selectedAgents).reduce(
    (data, curr) => {
      if (selectedAgents[curr].assigned) {
        data.assignedIds.push(curr);
      } else {
        data.unAssignedIds.push(curr);
      }

      return data;
    },
    {
      assignedIds: [] as string[],
      unAssignedIds: [] as string[],
    }
  );

  const allIdsSorted = customSort(selectedAgents, sortAgentsBy.name, SortOrder.ASCENDING, allAgentIds);
  const assignedIdsSorted = customSort(selectedAgents, sortAgentsBy.name, SortOrder.ASCENDING, assignedIds);

  const filteredAllIdsSorted = allIdsSorted.filter(filterBySearchTerm(searchTerm, selectedAgents));

  // Filtration agents based on active filters
  const suitedIds = filteredAllIdsSorted.filter((suitedId) => {
    // No active filter
    if (!assignedQueueIds.length && !assignedSkillIds.length) {
      return true;
    }

    const { queueIds, skillIds } = selectedAgents[suitedId];
    let includesQueues = true;
    let includesSkills = true;

    if (assignedQueueIds.length) {
      const includesQueuesArray = intersection(queueIds!, assignedQueueIds);
      includesQueues = includesQueuesArray.length > 0;
    }

    if (assignedSkillIds.length && skillIds) {
      const includesSkillsArray = intersection(skillIds, assignedSkillIds);
      includesSkills = includesSkillsArray.length > 0;
    }

    // Filtered agents has to have at least one selected Queue AND selected Skill.
    if (includesQueues && includesSkills) {
      return true;
    }
    return false;
  });

  // Extracting assigned and unassigned Ids[] only from suited items
  const assignedSuitedIds = suitedIds.filter((curr) => selectedAgents[curr].assigned);
  const unAssignedSuitedIds = suitedIds.filter((curr) => !selectedAgents[curr].assigned);

  const assignedUnSuitedIds = assignedIdsSorted.filter((curr) => !assignedSuitedIds.includes(curr));

  // TODO: Maybe think about more readable conditions set
  const emptyStatusMessage =
    !assignedQueueIds.length && !assignedSkillIds.length && !searchTerm
      ? 'person.noPeopleToSelect'
      : (assignedQueueIds.length || assignedSkillIds.length) && unAssignedSuitedIds.length === 0 && !searchTerm
      ? 'person.noPeopleToSelect'
      : 'filter.noResults';

  const toggleAllSuited = () =>
    unAssignedSuitedIds.length === 0
      ? assignedSuitedIds.map((id) => toggleItemCheckedState(id))
      : unAssignedSuitedIds.map((id) => toggleItemCheckedState(id));

  const toggleItemCheckedState = (id: string) => {
    setSelectedAgents((previouslySelectedItems) => ({
      ...previouslySelectedItems,
      [id]: {
        ...previouslySelectedItems[id],
        assigned: !previouslySelectedItems[id].assigned,
      },
    }));
  };

  const unAssignAll = () => assignedIds.map((id) => toggleItemCheckedState(id));

  return (
    <>
      <Legend>{t('person.availablePeople')}</Legend>
      <div className="persons-bulk-assign__search-input">
        <RenderInput
          dataTestId={PERSONS_BULK_ASSIGN_SEARCH}
          meta={{}}
          input={{
            name: 'persons-search',
            onChange: setSearchTerm,
            onBlur: () => ({}),
            onFocus: () => ({}),
            value: searchTerm,
            label: t('filter.search'),
          }}
        />
      </div>
      <div className="persons-bulk-assign__info-wrapper">
        {suitedIds.length > 0 && !searchTerm && (
          <Checkbox
            data-test-id={SELECT_ALL_SUITED_PERSONS_CHECKBOX}
            input={{ value: unAssignedSuitedIds.length === 0, name: '' }}
            onClick={toggleAllSuited}
          >
            {t('form.selectAll')}
          </Checkbox>
        )}
        {suitedIds.length > 0 && (
          <span className="ml-auto">
            {`${t('filter.results')} `}
            <strong data-test-id={SUITED_PERSONS_RESULT}>{suitedIds.length}</strong>
          </span>
        )}
      </div>
      <div data-test-id="separator" className="persons-bulk-assign__separator" />
      {(unAssignedIds.length === 0 || unAssignedSuitedIds.length === 0) && <Infobox>{t(emptyStatusMessage)}</Infobox>}
      <PerfectScrollbar
        className="persons-bulk-assign__checkbox-wrapper persons-bulk-assign__checkbox-wrapper--suited"
        options={{ suppressScrollX: true }}
      >
        {unAssignedSuitedIds.map((suitedId) => {
          const { id, name, surname, assigned } = selectedAgents[suitedId];
          return (
            <Checkbox
              data-test-id={getSuitedAgentCheckboxTag(id)}
              input={{ name, value: assigned }}
              key={id}
              onClick={() => toggleItemCheckedState(id)}
            >
              {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
              <Ellipsis>{formatNameOfPerson(name, surname)}</Ellipsis>
              <AgentDetail agent={selectedAgents[suitedId]} skills={skills} queues={queues} />
            </Checkbox>
          );
        })}
      </PerfectScrollbar>
      {assignedIds.length > 0 && (
        <>
          <div className="persons-bulk-assign__separator" />
          <PerfectScrollbar
            className="persons-bulk-assign__checkbox-wrapper persons-bulk-assign__checkbox-wrapper--assigned"
            options={{ suppressScrollX: true }}
          >
            {assignedSuitedIds.map((assignedId) => {
              const { id, name, surname, assigned } = selectedAgents[assignedId];
              return (
                <div key={id} className="checkbox-assigned checkbox-assigned--suited">
                  <Checkbox
                    data-test-id={getAssignedSuitedAgentCheckboxTag(id)}
                    input={{ name, value: assigned }}
                    onClick={() => toggleItemCheckedState(id)}
                  >
                    {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
                    <Ellipsis>{formatNameOfPerson(name, surname)}</Ellipsis>
                    <AgentDetail agent={selectedAgents[assignedId]} skills={skills} queues={queues} />
                  </Checkbox>
                </div>
              );
            })}

            {assignedUnSuitedIds.map((assignedId) => {
              const { id, name, surname, assigned } = selectedAgents[assignedId];
              return (
                <div key={id} className="checkbox-assigned checkbox-assigned--unsuited">
                  <Checkbox
                    data-test-id={getAssignedUnSuitedAgentCheckboxTag(id)}
                    input={{ name, value: assigned }}
                    onClick={() => toggleItemCheckedState(id)}
                  >
                    {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
                    <Ellipsis>{formatNameOfPerson(name, surname)}</Ellipsis>
                    <AgentDetail agent={selectedAgents[assignedId]} skills={skills} queues={queues} />
                  </Checkbox>
                </div>
              );
            })}
          </PerfectScrollbar>
          <div className="persons-bulk-assign__separator" />
          <div className="persons-bulk-assign__reset">
            <Button
              className="p-2"
              data-test-id={UNSELECT_ALL_SELECTED_PERSONS_CHECKBOX}
              kind="icon"
              onClick={unAssignAll}
            >
              <Icon kind="cross" size={15} color="grey3" className="mr-2" />
              {t('filter.reset')}
            </Button>
          </div>
        </>
      )}
    </>
  );
};
